/**
 * GLSL code and material creation for stroked wide lines.
 * @module
 */
import stableHash from "common/utils/stableHash";
import * as THREE from "three";
import { defaultStencilProperties } from "../defaultStencilProperties";
import type { HighlightDataTexture } from "../HighlightDataTexture";
import {
  entityIdHighlightDeclarationsFragment,
  entityIdHighlightDeclarationsVertex,
  entityIdHighlightFragment,
  entityIdHighlightVertexNoMix,
} from "./shaders/entityIdHighlight";

const LineShader = {
  vertexShader: `precision highp float;

attribute float distance;
attribute float texOffset;  // The sign signals offset from line center. In addition values > 1 signal disabling per-vertex distance offset.

uniform float linewidth;

// Width of one pixel in world coordinates.
uniform float worldToPixelFactor;

${entityIdHighlightDeclarationsVertex}

varying float vDistance;
varying float vTexOffset;

void main() {
#if defined(LINEWIDTH_IN_PIXELS)
    float width = linewidth * worldToPixelFactor;
#else
    // Assume uniform scaling
    float modelScale = length(vec3(modelViewMatrix[0][0], modelViewMatrix[1][0], modelViewMatrix[2][0]));
    float width = max(linewidth, worldToPixelFactor / modelScale * 0.5);
#endif

    // Compute the distance of the vertex from the normal of the line at the originating vertex.
    float distanceOffset = 0.0;
    if (abs(texOffset) < 1.5) {
      float distanceOffsetSqr = pow(length(normal), 2.0) - 1.0;
      distanceOffset = distanceOffsetSqr > 0.0 ? sqrt(distanceOffsetSqr) * width : 0.0;
    }

    vDistance = abs(distance) + sign(distance) * distanceOffset;
    vTexOffset = sign(texOffset);

    vec3 delta = normal * width;
    vec4 pos = modelViewMatrix * vec4(position + delta, 1.0);
    gl_Position = projectionMatrix * vec4(pos.xyz, 1.0);

    ${entityIdHighlightVertexNoMix}
}`,

  fragmentShader: `precision highp float;

varying float vDistance;
varying float vTexOffset;

uniform vec3 color;
uniform bool dashed;
uniform float invDashInterval;
uniform mat4 dashPatternNormalized;
uniform bool dashIntervalRelativeToWidth;

uniform float linewidth;

#if defined(LINEWIDTH_IN_PIXELS)
uniform float worldToPixelFactor;
#endif

${entityIdHighlightDeclarationsFragment}

bool isInGap(float dashT) {
  // We use this ugly unrolled code since WebGL 1.0 GLSL doesn't support dynamic access of matrix data.
  float dashPatternSum = dashPatternNormalized[0][0];
  if (dashPatternSum >= dashT) {
    return false;
  }
  dashPatternSum += dashPatternNormalized[0][1];
  if (dashPatternSum >= dashT) {
    return true;
  }
  dashPatternSum += dashPatternNormalized[0][2];
  if (dashPatternSum >= dashT) {
    return false;
  }
  dashPatternSum += dashPatternNormalized[0][3];
  if (dashPatternSum >= dashT) {
    return true;
  }
  dashPatternSum += dashPatternNormalized[1][0];
  if (dashPatternSum >= dashT) {
    return false;
  }
  dashPatternSum += dashPatternNormalized[1][1];
  if (dashPatternSum >= dashT) {
    return true;
  }
  dashPatternSum += dashPatternNormalized[1][2];
  if (dashPatternSum >= dashT) {
    return false;
  }
  dashPatternSum += dashPatternNormalized[1][3];
  if (dashPatternSum >= dashT) {
    return true;
  }
  dashPatternSum += dashPatternNormalized[2][0];
  if (dashPatternSum >= dashT) {
    return false;
  }
  dashPatternSum += dashPatternNormalized[2][1];
  if (dashPatternSum >= dashT) {
    return true;
  }
  dashPatternSum += dashPatternNormalized[2][2];
  if (dashPatternSum >= dashT) {
    return false;
  }
  dashPatternSum += dashPatternNormalized[2][3];
  if (dashPatternSum >= dashT) {
    return true;
  }
  dashPatternSum += dashPatternNormalized[3][0];
  if (dashPatternSum >= dashT) {
    return false;
  }
  dashPatternSum += dashPatternNormalized[3][1];
  if (dashPatternSum >= dashT) {
    return true;
  }
  dashPatternSum += dashPatternNormalized[3][2];
  if (dashPatternSum >= dashT) {
    return false;
  }
  dashPatternSum += dashPatternNormalized[3][3];
  if (dashPatternSum >= dashT) {
    return true;
  }
  return false;
}

void main() {
  float useInvDashInterval = invDashInterval;
  if ( dashIntervalRelativeToWidth ) {
    #if defined(LINEWIDTH_IN_PIXELS)
      float width = linewidth * worldToPixelFactor;
    #else
      float width = linewidth;
    #endif
    useInvDashInterval /= width;
  }

#ifdef DEBUG_DISTANCE
  gl_FragColor = vec4(fract(vDistance * 0.2), fract(vDistance * 0.01), fract(vDistance * 0.001), 1.0);
#elif defined(DEBUG_TEX)
  gl_FragColor = vec4(vTexOffset * 0.5 + 0.5, -vTexOffset * 0.5 + 0.5, 0.0, 1.0);
#else
  if (dashed) {
    float dashT = fract(vDistance * useInvDashInterval);
    if (isInGap(dashT)) {
      discard;
    }
  }
  gl_FragColor = vec4(color, 1.0);
#endif
  ${entityIdHighlightFragment}

  #include <colorspace_fragment>
}`,
  uniforms: {
    color: { value: new THREE.Color(0, 0, 0) },
    linewidth: { value: 0.2 },
    worldToPixelFactor: { value: 1 },
    zoom: { value: 1 },
    highlightedIds: { value: null },
    dashed: { value: false },
    invDashInterval: { value: 0.5 },
    dashPatternNormalized: { value: new THREE.Matrix4().set(0.5, 0.5, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0) }, // Contains relative dash and space lengths. Total sum of the values should be 1.
    dashIntervalRelativeToWidth: { value: true },
  },
} as const;

export const DefaultLineStrokeMaterialOptions: LineStrokeMaterialOptions = {
  color: 0x000,
  linewidth: 0.06,
  linewidthInPixels: false,
  dashed: false,
  dashPattern: [1.5, 1.5],
  dashIntervalRelativeToWidth: false,
  depthTest: true,
};

export interface LineStrokeMaterialOptions {
  readonly color: THREE.Color | number | string;
  readonly linewidth: number;
  readonly linewidthInPixels: boolean;
  readonly dashed: boolean;
  readonly dashPattern: number[]; // An array recording lengths of dashes as well as lengths of gaps.
  readonly dashIntervalRelativeToWidth: boolean;
  readonly depthTest: boolean;
}

export class LineStrokeMaterial extends THREE.ShaderMaterial {
  constructor(
    opts: Partial<LineStrokeMaterialOptions>,
    highlightDataTextureUniform: THREE.IUniform,
    isolateModeUniform: THREE.IUniform,
  ) {
    const { color, linewidth, linewidthInPixels, dashed, dashPattern, dashIntervalRelativeToWidth, depthTest } = {
      ...DefaultLineStrokeMaterialOptions,
      ...opts,
    };

    if (!(dashPattern.length <= 16)) {
      console.assert(false, "Dash pattern is passed to the shader in a fixed size array");
    }

    const superParams: THREE.ShaderMaterialParameters = {
      ...defaultStencilProperties,
      vertexShader: LineShader.vertexShader,
      fragmentShader: LineShader.fragmentShader,
      uniforms: THREE.UniformsUtils.clone(LineShader.uniforms),
      transparent: true, // The transparent flag affects render order, so we always set 2D objects as transparent.
      blending: THREE.NormalBlending,
      depthTest,
      depthWrite: true,
      side: THREE.DoubleSide,
      name: "LineStrokeMaterial",
    };

    if (linewidthInPixels) {
      superParams.defines = { LINEWIDTH_IN_PIXELS: 1 };
    }

    super(superParams);

    const dashPatternSum = dashPattern.reduce((prevValue, currentValue) => prevValue + currentValue, 0);

    const dashPatternNormalized = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    const dashPatternLen = Math.min(dashPattern.length, 16);
    for (let i = 0; i < dashPatternLen; ++i) {
      dashPatternNormalized[i] = dashPattern[i] / dashPatternSum;
    }

    // Counter potential float precision errors
    dashPatternNormalized[dashPatternLen - 1] *= 1.1;

    this.uniforms.highlightedIds = highlightDataTextureUniform;
    this.uniforms.isolateMode = isolateModeUniform;
    this.uniforms.dashed.value = dashed;
    (this.uniforms.dashPatternNormalized.value as THREE.Matrix4).fromArray(dashPatternNormalized);
    this.uniforms.invDashInterval.value = 1.0 / dashPatternSum;
    this.uniforms.dashIntervalRelativeToWidth.value = dashIntervalRelativeToWidth;
    this.setColor(color instanceof THREE.Color ? color : new THREE.Color(color));
    this.setLinewidth(linewidth);
  }

  setWorldToPixelFactor(value: number) {
    this.uniforms.worldToPixelFactor.value = value;
  }

  setColor(value: THREE.Color) {
    this.uniforms.color.value = value;
  }

  setLinewidth(value: number) {
    this.uniforms.linewidth.value = value * 3.0;
  }
}

export class LineStrokeMaterialFactory {
  private worldToPixelFactor: number = 0.001;
  private materials: { [key: string]: LineStrokeMaterial } = {};

  private static _instance: LineStrokeMaterialFactory | undefined;

  static get instance(): LineStrokeMaterialFactory {
    return this._instance ?? (this._instance = new LineStrokeMaterialFactory());
  }

  private highlightDataTextureUniform: THREE.IUniform = { value: null };
  private isolateModeUniform: THREE.IUniform = { value: false };

  private disabledHighlightDataTextureUniform: THREE.IUniform = { value: null };
  private disabledIsolateModeUniform: THREE.IUniform = { value: false };

  private constructor() {}

  getMaterial(options: Partial<LineStrokeMaterialOptions>, useHighlighting: boolean = true): LineStrokeMaterial {
    const hash = stableHash({ ...options, useHighlighting }); // TODO normalize color
    if (this.materials[hash]) {
      return this.materials[hash];
    }

    const material = (this.materials[hash] = new LineStrokeMaterial(
      options,
      useHighlighting ? this.highlightDataTextureUniform : this.disabledHighlightDataTextureUniform,
      useHighlighting ? this.isolateModeUniform : this.disabledIsolateModeUniform,
    ));
    material.defines.ISOLATE_MODE = 1;
    material.setWorldToPixelFactor(this.worldToPixelFactor);

    return material;
  }

  setWorldToPixelFactor(factor: number) {
    if (this.worldToPixelFactor === factor) {
      return;
    }
    this.worldToPixelFactor = factor;
    for (const id in this.materials) {
      const mat = this.materials[id];
      mat.setWorldToPixelFactor(factor);
    }
  }

  prepareRender(highlightDataTexture?: HighlightDataTexture) {
    this.highlightDataTextureUniform.value = highlightDataTexture?.texture ?? null;
    this.isolateModeUniform.value = highlightDataTexture?.isolateMode ?? false;
  }
}
